import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Badge, Button, Dialog, EmptyState, Skeleton } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { rbacApi } from '../../rbac/api/rbac.api.js';
import { scopeTypeLabel, scopeTypeOf, type ScopeOption } from '../../rbac/ui/ScopePicker.js';
import type { StaffDto } from '../api/staff.api.js';

export interface StaffPermissionsDialogProps {
  staff: StaffDto | null;
  scopeOptions: readonly ScopeOption[];
  onClose: () => void;
}

/**
 * Xodimning HAQIQIY ruxsatlari — rol nomi emas, kod darajasida.
 *
 * Nega kerak: "Skladchi" degan rol nomi hech narsa kafolatlamaydi — o'sha
 * rolga kecha yangi ruxsat qo'shilgan bo'lishi mumkin. Nazoratchi savoli
 * har doim aniq: "shu odam nima qila oladi?"
 *
 * Ruxsatlar DOIRA bilan birga ko'rsatiladi va bu shart: `stock.issue` ning
 * o'zi hech narsa aytmaydi, "101-omborda `stock.issue`" esa to'liq javob.
 * Ikkalasini ajratib ko'rsatish RBAC ni noto'g'ri o'qishga olib borardi.
 *
 * Ma'lumot ikki so'rovdan yig'iladi (biriktiruvlar + rollar) — backendda
 * "xodimning yig'ma ruxsatlari" endpointi yo'q va u shu ekran uchun
 * yozilishi ham shart emas: ikkala ro'yxat baribir keshda turadi.
 */
export const StaffPermissionsDialog = ({
  staff,
  scopeOptions,
  onClose,
}: StaffPermissionsDialogProps) => {
  const userId = staff?.id ?? null;

  const assignments = useQuery({
    queryKey: ['rbac', 'assignments', userId],
    queryFn: () => rbacApi.listAssignments(userId ?? ''),
    enabled: userId !== null,
  });

  const roles = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: () => rbacApi.listRoles(),
    enabled: userId !== null,
  });

  const groups = useMemo(() => {
    const byId = new Map((roles.data?.items ?? []).map((role) => [role.id, role]));
    return (assignments.data ?? []).map((assignment) => {
      const role = byId.get(assignment.roleId);
      const option = scopeOptions.find(
        (o) => scopeTypeOf(o.kind) === assignment.scope.type && o.id === assignment.scope.id,
      );
      return {
        id: assignment.id,
        roleName: assignment.roleName,
        scopeText:
          assignment.scope.type === 'GLOBAL'
            ? scopeTypeLabel('GLOBAL')
            : `${scopeTypeLabel(assignment.scope.type)}: ${option?.label ?? `#${assignment.scope.id ?? '—'}`}`,
        permissions: role?.permissions ?? [],
      };
    });
  }, [assignments.data, roles.data, scopeOptions]);

  const pending = assignments.isPending || roles.isPending;

  return (
    <Dialog
      open={staff !== null}
      onClose={onClose}
      size="lg"
      title={t('staff.permissionsTitle', { name: staff?.fullName ?? '' })}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('staff.close')}
        </Button>
      }
    >
      {staff !== null && staff.isSuperadmin ? (
        /* Superadminda biriktiruv YO'Q va bo'lishi ham shart emas: u
           tekshiruvdan butunlay chetlab o'tadi. Buni aytmasak, bo'sh ro'yxat
           "ruxsati yo'q" degan teskari xulosaga olib borardi. */
        <div className="flex items-start gap-3 rounded-[var(--radius-sm)] border border-warn/30 bg-warn-soft p-4">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-warn" aria-hidden />
          <p className="text-sm text-text">{t('staff.superadminNote')}</p>
        </div>
      ) : pending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          title={t('staff.noPermissionsTitle')}
          description={t('staff.noPermissionsHint')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section
              key={group.id}
              className="flex flex-col gap-2 rounded-[var(--radius-sm)] border border-border p-3"
            >
              <header className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-text">{group.roleName}</span>
                <Badge tone="info">{group.scopeText}</Badge>
              </header>

              {group.permissions.length === 0 ? (
                <p className="text-sm text-text-muted">{t('staff.roleNoPermissions')}</p>
              ) : (
                <ul className="flex flex-wrap gap-1.5">
                  {group.permissions.map((permission) => (
                    <li
                      key={permission}
                      className="rounded-[var(--radius-sm)] border border-border bg-surface-2 px-2 py-0.5 font-mono text-xs text-text-muted"
                    >
                      {permission}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}
    </Dialog>
  );
};
